import * as Yup from 'yup';
import * as R from 'ramda';
import React from 'react';
import { withFormik } from 'formik';
import { pure, compose, withProps, withHandlers } from 'react-recompose';
// components
import { ChargeFormFooter } from '../../../components/form-footer';
import PriceSheetItems from '../../../components/price-sheet-items';
import { OrderChargesTotal } from '../../../components/charge/components';
import { DOChargeComponent2 } from '../../../components/charge/formik/charges-array';
import { withRecalculateAllChargesId, getSharedAccessorialsApplyTo } from '../../../components/charge/hocs';
import { withAsyncChargeAccessorials } from '../../../components/charge/hocs/with-async-charge-accessorials';
// features
import PC from '../../permission/role-permission';
import SectionDivider from '../../new-do/components/section-divider';
// forms
import { Fieldset2 } from '../../../forms';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { asyncGetAccessorialsList } from '../../../helpers/api-async';
// hocs
import {
  withAsyncConfigs,
  withConnectModalAndLoaderActions,
  withAsyncGetTransportationModeGrouping,
} from '../../../hocs';
// schemes
import * as VS from '../../../schemes';
// ui
import { Box, Flex, StickedBox, MainDarkActionButton } from '../../../ui';
// feature rate
import { withOrderRateSyncWithTrips, withHandleCloRateAvailableRates } from './hocs';
import { setDefaultCustomerRateValues, getTotalQuantitiesFromValues } from '../helpers';
import { withApplyOrderRateUpliftForm } from '../components/apply-order-rate-uplift-form';
import {
  laneIdField,
  maxTripRateField,
  mainCustomerRateFields,
  totalCustomerRateFields,
  contactCustomerRateFields,
} from './settings';
//////////////////////////////////////////////////

export const validationSchemaCustomerRate = Yup.object()
  .shape(VS.validationSchemaTripServiceObject)
  .shape(VS.validationSchemaTripTotalObject)
  .shape(VS.validationSchemaContactObject);

const CustomerCharges = (props) => {
  const {
    services,
    branchGuid,
    isEditMode,
    isCreateMode,
    stopsQuantity,
    addMXLineHaul,
    recalculateAllChargesId,
    defaultOrderAccessorials,
    sharedAccessorialsApplyTo,
    defaultOrderFuelAccessorial,
  } = props;

  const commonTotalProps = G.getTotalsFromValues(props);

  const currency = R.pathOr('', ['values', GC.FIELD_CURRENCY], props);
  const serviceDays = R.pathOr('', ['values', GC.FIELD_SERVICE_DAYS], props);
  const totalQuantities = getTotalQuantitiesFromValues(R.prop('values', props));
  const normalizedTotal = R.pathOr({}, ['values', GC.SYSTEM_OBJECT_NORMALIZED_TOTAL], props);

  return (
    <div>
      <DOChargeComponent2
        {...commonTotalProps}
        rateProps={props}
        isCustomer={true}
        currency={currency}
        services={services}
        branchGuid={branchGuid}
        loadType={GC.FIELD_CLO}
        isEditMode={isEditMode}
        serviceDays={serviceDays}
        isCreateMode={isCreateMode}
        stopsQuantity={stopsQuantity}
        addMXLineHaul={addMXLineHaul}
        normalizedTotal={normalizedTotal}
        totalQuantities={totalQuantities}
        chargesArrayName={GC.FIELD_CHARGES}
        recalculateAllChargesId={recalculateAllChargesId}
        defaultOrderAccessorials={defaultOrderAccessorials}
        sharedAccessorialsApplyTo={sharedAccessorialsApplyTo}
        defaultOrderFuelAccessorial={defaultOrderFuelAccessorial}
      />
    </div>
  );
};

const fieldsWrapperStyles = { px: 15 };

const CustomerRateForm = (props) => {
  const {
    values,
    isLegRate,
    isEditMode,
    formBorder,
    width = 755,
    handleSubmit,
    asyncConfigs,
    initialValues,
    cancelBtnStyles,
    submitBtnStyles,
    handleCustomChange,
    showSyncWithTripsBtn,
    showAvailableRatesBtn,
    handleOpenAutoRateModal,
    handleApplyOrderRateUpliftForm,
    handleSetRecalculateAllChargesId,
    handleGetSharedAccessorialsApplyTo,
  } = props;

  const charges = G.getChargesFromObject(values);

  const useTripMaxPay = R.or(
    G.getConfigValueFromStore(GC.RATE_ENGINE_USE_TRIP_MAX_PAY, asyncConfigs),
    G.isNotNilAndNotEmpty(G.getPropFromObject(GC.FIELD_TRIP_MAX_PAY, initialValues)),
  );

  return (
    <Box bg='white' width={width}>
      <StickedBox top='0px' zIndex={15}>
        <Flex
          p='7px 10px'
          bg='offWhite'
          color='dark.mainDark'
          justifyContent='space-between'
        >
          <Box fontWeight={700}>
            {
              isLegRate
                ? G.getWindowLocale('titles:leg-rate', 'Leg Rate')
                : G.getWindowLocale('titles:customer-rate', 'Customer Rate')
            }
          </Box>
          <Flex gap='5px'>
            <MainDarkActionButton onClick={handleSetRecalculateAllChargesId}>
              {G.getWindowLocale('titles:recalc-charges', 'Recalc Charges')}
            </MainDarkActionButton>
            <MainDarkActionButton onClick={handleGetSharedAccessorialsApplyTo}>
              {G.getWindowLocale('titles:update-auto-acc', 'Update Auto Acc.')}
            </MainDarkActionButton>
            {showSyncWithTripsBtn && (
              <MainDarkActionButton onClick={handleApplyOrderRateUpliftForm}>
                {G.getWindowLocale('titles:sync-with-trips', 'Sync With Trips')}
              </MainDarkActionButton>
            )}
            {showAvailableRatesBtn && (
              <MainDarkActionButton onClick={handleOpenAutoRateModal}>
                {G.getWindowLocale('titles:show-rates', 'Show Rates')}
              </MainDarkActionButton>
            )}
          </Flex>
        </Flex>
      </StickedBox>
      <form onSubmit={handleSubmit}>
        <Box pt={20} border={formBorder}>
          {
            G.hasAmousCurrentUserPermissions(PC.CLO_RATE_LANE_ID_EXECUTE) &&
            <Fieldset2
              {...G.getFormikProps(props)}
              fields={laneIdField}
              fieldsWrapperStyles={fieldsWrapperStyles}
            />
          }
          <Fieldset2
            {...G.getFormikProps(props)}
            fields={mainCustomerRateFields}
            handleCustomChange={handleCustomChange}
            fieldsWrapperStyles={fieldsWrapperStyles}
            {...G.getModeServiceFromAsyncConfigsForRateSelect(props)}
          />
          <SectionDivider
            mt='0px'
            text={G.getWindowLocale('titles:totals', 'Totals', { caseAction: 'upperCase' })}
          />
          <Fieldset2
            {...G.getFormikProps(props)}
            fields={totalCustomerRateFields}
            fieldsWrapperStyles={fieldsWrapperStyles}
          />
          <SectionDivider
            text={G.getWindowLocale('titles:contact-info', 'Contact Info', { caseAction: 'upperCase' })}
          />
          <Fieldset2
            {...G.getFormikProps(props)}
            fields={contactCustomerRateFields}
            fieldsWrapperStyles={fieldsWrapperStyles}
          />
          {
            useTripMaxPay &&
            <SectionDivider />
          }
          {
            useTripMaxPay &&
            <Fieldset2
              {...G.getFormikProps(props)}
              fields={R.of(Array, maxTripRateField)}
              fieldsWrapperStyles={fieldsWrapperStyles}
            />
          }
          {
            isEditMode &&
            <PriceSheetItems
              values={values}
              additionalFormHeaderSectionStyles={{ my: 0, m: '20px 0 10px 0' }}
            />
          }
          <CustomerCharges {...props} />
          <StickedBox bottom='0px' zIndex={13}>
            <OrderChargesTotal {...props} charges={charges} />
            <ChargeFormFooter cancelBtnStyles={cancelBtnStyles} submitBtnStyles={submitBtnStyles} />
          </StickedBox>
        </Box>
      </form>
    </Box>
  );
};

const configsNamesArray = [
  GC.GENERAL_MODE_TRANSPORTATION,
  GC.RATE_ENGINE_USE_TRIP_MAX_PAY,
  GC.GENERAL_TRANSPORTATION_SERVICE_TYPE,
  GC.RATE_ENGINE_TIME_RATE_END_DATE_TYPE,
  GC.RATE_ENGINE_TIME_RATE_START_DATE_TYPE,
];

const withSharedAccessorialsApplyToAndCustomChange = withHandlers({
  handleGetSharedAccessorialsApplyTo: (props) => async () => {
    const {
      values,
      setValues,
      branchGuid,
      stopsQuantity,
      handleSetRecalculateAllChargesId,
    } = props;

    const accessorials = await asyncGetAccessorialsList(branchGuid, GC.ACCESSORIAL_TYPE_CUSTOMER_RATE_INVOICE);

    const sharedApplyTo = await getSharedAccessorialsApplyTo(
      { ...props, accessorials, stopCount: stopsQuantity },
      branchGuid,
    );

    const sharedApplyToFiltered = G.filterChargesByModeOrNoMode(sharedApplyTo, R.prop(GC.FIELD_MODE, values));

    const rateCharges = R.pathOr([], [GC.FIELD_CHARGES], values);
    const charges = G.replaceAutoChargesWithNewSharedAssessorialsApplyTo(rateCharges, sharedApplyToFiltered);
    const newValues = R.assoc(GC.FIELD_CHARGES, charges, values);

    setValues(newValues);

    handleSetRecalculateAllChargesId();
  },
  handleCustomChange: (props) => (e) => {
    const { values, setValues, isEditMode, sharedAccessorialsApplyTo } = props;

    const { charges } = values;

    const value = G.getEventTargetValue(e);

    const sharedApplyToFiltered = R.or(isEditMode, G.isNilOrEmpty(R.prop(GC.FIELD_MODE, values))) ?
        [] : G.filterChargesByMode(sharedAccessorialsApplyTo, value);

    setValues({
      ...values,
      [GC.FIELD_MODE]: value,
      [GC.FIELD_CHARGES]: G.isNilOrEmpty(charges) ?
        sharedApplyToFiltered : [...G.filterChargesByModeOrNoMode(charges, value), ...sharedApplyToFiltered],
    });
  },
});

const enhanceCreate = compose(
  withRecalculateAllChargesId,
  withConnectModalAndLoaderActions,
  withProps(({ cloGuid, telGuid }) => ({
    isEditMode: false,
    configsNamesArray,
    showAvailableRatesBtn: true,
    showSyncWithTripsBtn: R.or(
      G.isNotNilAndNotEmpty(cloGuid),
      G.isNotNilAndNotEmpty(telGuid),
    ),
  })),
  withAsyncConfigs,
  withAsyncChargeAccessorials({ isCustomer: true, isCreateMode: true }),
  withFormik({
    enableReinitialize: true,
    handleSubmit: (values, { props }) => {
      const { handleSendCloRate } = props;

      handleSendCloRate(G.mapObjectEmptyStringFieldsToNull(G.omitEmptyChargesFromData(values, GC.FIELD_CHARGES)));

      if (G.isNotNilAndNotEmpty(G.getPropFromObject('telCarrierRate', values))) {
        const message = G.getWindowLocale('messages:infoCarrierRatesCanAlsoBeChanged');

        G.showToastrMessageSimple('info', message);
      }
    },
    displayName: 'ADD_CUSTOMER_RATE_FORM',
    validationSchema: validationSchemaCustomerRate,
    mapPropsToValues: (props) => setDefaultCustomerRateValues(props),
  }),
  withHandleCloRateAvailableRates,
  withOrderRateSyncWithTrips({ updateValues: true }),
  withApplyOrderRateUpliftForm({ submitActionName: 'handleSyncWithTrips' }),
  withSharedAccessorialsApplyToAndCustomChange,
  pure,
);

const enhanceUpdate = compose(
  withRecalculateAllChargesId,
  withConnectModalAndLoaderActions,
  withProps(() => ({
    isEditMode: true,
    configsNamesArray,
    showSyncWithTripsBtn: true,
    showAvailableRatesBtn: true,
  })),
  withAsyncConfigs,
  withAsyncGetTransportationModeGrouping,
  withAsyncChargeAccessorials({ isCustomer: true, isCreateMode: false }),
  withFormik({
    enableReinitialize: true,
    validationSchema: validationSchemaCustomerRate,
    mapPropsToValues: (props) => setDefaultCustomerRateValues(props),
    handleSubmit: (values, { props }) => {
      const { selected, transportationMode } = values;

      const { initialValues, handleSendCloRate, handleCheckModeGroupingAndShowConfirmMessage } = props;

      const callback = (loadData) => handleSendCloRate(
        G.mapObjectEmptyStringFieldsToNull(G.omitEmptyChargesFromData(values, GC.FIELD_CHARGES)),
        loadData,
      );

      if (G.isNotNilAndNotEmpty(G.getPropFromObject('telCarrierRate', values))) {
        const message = G.getWindowLocale('messages:infoCarrierRatesCanAlsoBeChanged');

        G.showToastrMessageSimple('info', message);
      }

      if (R.or(
        G.isFalse(selected),
        R.pathEq(transportationMode, [GC.FIELD_MODE, GC.FIELD_DROPDOWN_OPTION_GUID], initialValues),
      )) return callback();

      handleCheckModeGroupingAndShowConfirmMessage({ callback, transportationMode });
    },
    displayName: 'EDIT_CUSTOMER_RATE_FORM',
  }),
  withHandleCloRateAvailableRates,
  withOrderRateSyncWithTrips({ updateValues: true }),
  withApplyOrderRateUpliftForm({ submitActionName: 'handleSyncWithTrips' }),
  withSharedAccessorialsApplyToAndCustomChange,
  pure,
);

export default enhanceCreate(CustomerRateForm);

const EditCustomerRateForm = enhanceUpdate(CustomerRateForm);

export {
  enhanceUpdate,
  CustomerRateForm,
  EditCustomerRateForm,
};
